<script setup lang="ts">
import { computed } from 'vue'
import { formatDate } from '@/utils'

const props = defineProps<{
    activities: ({ date: string; isCorrectAnswer: boolean } | string)[]
}>()

const dayLetters = [ 'M', 'T', 'W', 'T', 'F', 'Sa', 'Su' ]

const activityLib = computed(() => {
    return props.activities.reduce((acc, a) => {
        const date = typeof a === 'string' ? a : a.date

        acc[formatDate(date)] = a
        return acc
    }, {} as { [key: string]: { date: string; isCorrectAnswer: boolean } | string })
})

const dayData = computed(() => {
    const today = new Date()
    const monday = new Date().setDate(today.getDate() - today.getDay() + 1)
    const calStart = new Date(new Date(monday).setDate(new Date(monday).getDate() - 14))

    const days = new Array(28).fill('').map(() => {
        const day = new Date(calStart)
        calStart.setDate(calStart.getDate() + 1)

        return day
    })

    return days.map(day => {
        const activity = activityLib.value[formatDate(day)]
        const prevDay = formatDate(new Date(new Date(day).setDate(day.getDate() - 1)))
        const nextDay = formatDate(new Date(new Date(day).setDate(day.getDate() + 1)))
        const type = activity
            ? typeof activity === 'string'
                ? 'streak'
                : activity.isCorrectAnswer
                    ? 'correct'
                    : 'incorrect'
            : undefined

        return {
            date: day,
            type,
            consecutivePrev: activityLib.value[prevDay],
            consecutiveNext: activityLib.value[nextDay],
        }
    })
})
</script>

<template>
    <div class="calendar">
        <div
            v-for="(dl, index) in dayLetters"
            :key="index"
            class="calendar__day calendar__day--head"
        >
            {{ dl }}
        </div>
        <div
            v-for="day in dayData"
            :key="day.date.getTime()"
            class="calendar__day"
        >
            <div
                class="calendar__day-inner"
                :class="{
                    'calendar__day-inner--current': day.date.getDate() === new Date().getDate(),
                    'calendar__day-inner--future': day.date.getTime() > new Date().getTime(),
                    'calendar__day-inner--correct': day.type === 'correct',
                    'calendar__day-inner--incorrect': day.type === 'incorrect',
                    'calendar__day-inner--streak': day.type === 'streak',
                    'calendar__day-inner--streak-prev': day.consecutivePrev,
                    'calendar__day-inner--streak-next': day.consecutiveNext,
                }"
            >
                {{ day.date.getDate() }}
                <div
                    v-if="day.date.getDate() === new Date().getDate()"
                    class="calendar__day-inner--current-underline"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.calendar {
    display: flex;
    flex-wrap: wrap;
    max-width: 270px;
    margin: 0 auto;

    .calendar__day {
        text-align: center;
        width: 14.2857%;
        height: 14.2857%;
        color: $pewter;
        font-size: 13.28px;
        letter-spacing: -0.47px;
        font-weight: 600;

        &--head {
            font-size: 10px;
            letter-spacing: 0.4px;
            margin-bottom: 8px;
        }

        .calendar__day-inner {
            background-color: $gray-background;
            border-radius: 100px;
            width: 32px;
            height: 32px;
            margin: 0 auto 11px;
            box-sizing: border-box;
            line-height: 34px;
            position: relative;

            &--current {
                color: $brand-black;
                background: $white;

                .calendar__day-inner--current-underline {
                    content: '';
                    bottom: 5px;
                    left: 50%;
                    margin-left: -5px;
                    width: 10px;
                    height: 2px;
                    background: $brand-black;
                    position: absolute;
                }
            }

            &--correct {
                background: $green;
                color: $white;

                .calendar__day-inner--current-underline {
                    background: $white;
                }
            }

            &--incorrect {
                border: 1px solid $brand-red;
                background: $white;
                color: $brand-red;

                .calendar__day-inner--current-underline {
                    background: $brand-red;
                }
            }

            &--streak {
                background: $brand-black;
                color: $white;

                &.calendar__day-inner--streak-next::after,
                &.calendar__day-inner--streak-prev::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    margin-top: -1px;
                    height: 3px;
                    width: 8px;
                    background: $brand-black;
                }

                &.calendar__day-inner--streak-prev::before {
                    left: -5px;
                }

                &.calendar__day-inner--streak-next::after {
                    right: -5px;
                }

                .calendar__day-inner--current-underline {
                    background: $white;
                }
            }

            &--future {
                background: $white;
            }
        }
    }
}
</style>
